import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  UserIcon, 
  EnvelopeIcon, 
  LockClosedIcon,
  PhoneIcon,
  BuildingStorefrontIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  ArrowLeftIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import useAuthStore from '../stores/useAuthStore';
import toast from 'react-hot-toast';
import restaurantLogo from '../assets/restaurant-logo.png';

const Auth = () => {
  const navigate = useNavigate();
  const { signIn, signUp } = useAuthStore();
  const [isLogin, setIsLogin] = useState(true);
  const [selectedRole, setSelectedRole] = useState('restaurant_owner');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    phone: ''
  });

  const roles = [
    {
      id: 'restaurant_owner',
      name: 'Restaurant Owner',
      description: 'Manage restaurant, menu, and staff',
      icon: BuildingStorefrontIcon
    },
    {
      id: 'staff',
      name: 'Restaurant Staff',
      description: 'Apply to work at a restaurant',
      icon: UserGroupIcon
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const redirectToRoleDashboard = (role) => {
    switch (role) {
      case 'staff':
        navigate('/staff');
        break;
      case 'restaurant_owner':
        navigate('/dashboard');
        break;
      case 'super_admin':
        navigate('/admin');
        break;
      default:
        navigate('/dashboard');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const result = await signIn(formData.email, formData.password);
        if (result.error) {
          toast.error(result.error.message || 'Login failed');
          return;
        }
        if (result.data?.user) {
          toast.success('Login successful!');
          redirectToRoleDashboard(result.data.user.role);
        }
      } else {
        const result = await signUp(formData.email, formData.password, {
          full_name: formData.fullName,
          phone: formData.phone,
          role: selectedRole
        });

        if (result.success) {
          toast.success('Account created successfully! Please login.');
          setIsLogin(true);
          setFormData({ email: formData.email, password: '', fullName: '', phone: '' });
        } else {
          toast.error(result.error || 'Signup failed');
        }
      }
    } catch (error) {
      toast.error(error.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white font-sans selection:bg-[#F5A623] selection:text-black flex items-center justify-center p-6 sm:p-8 relative overflow-hidden">
      {/* Subtle Background Glows */}
      <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-[#F5A623] blur-[150px] opacity-[0.05] rounded-full pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-[#F5A623] blur-[150px] opacity-[0.05] rounded-full pointer-events-none"></div>

      {/* Back Button */}
      <motion.button
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={() => navigate('/')}
        className="fixed top-8 left-8 z-50 flex items-center gap-2 text-sm font-bold text-gray-400 hover:text-white transition-colors group"
      >
        <div className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center group-hover:border-white/30 transition-colors">
          <ArrowLeftIcon className="w-4 h-4" />
        </div>
        Back
      </motion.button>

      <div className="w-full max-w-lg relative z-10">
        {/* Animated Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="bg-[#111111] border border-white/10 p-8 sm:p-12 rounded-3xl shadow-2xl relative overflow-hidden"
        >
          {/* Top Logo */}
          <div className="flex justify-center mb-10">
            <img 
              src={restaurantLogo} 
              alt="Ordyrr Logo" 
              className="h-10 md:h-12 w-auto object-contain drop-shadow-[0_0_8px_rgba(255,255,255,0.1)]" 
            />
          </div>

          <div className="text-center mb-10">
            <h1 className="text-3xl md:text-4xl font-black tracking-tighter mb-3">
              {isLogin ? 'Welcome Back' : 'Get Started'}
            </h1>
            <p className="text-gray-500 font-light text-sm md:text-base">
              {isLogin ? 'Enter your credentials to access your dashboard' : 'Join Ordyrr and modernize your restaurant operations'}
            </p>
          </div>

          {/* Role Selection for Signup */}
          <AnimatePresence mode="wait">
            {!isLogin && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-10 overflow-hidden"
              >
                <div className="grid grid-cols-2 gap-4">
                  {roles.map((role) => (
                    <button
                      key={role.id}
                      type="button"
                      onClick={() => setSelectedRole(role.id)}
                      className={`p-4 rounded-xl border transition-all text-left relative group ${
                        selectedRole === role.id 
                          ? 'bg-[#F5A623]/5 border-[#F5A623] text-white' 
                          : 'bg-[#0A0A0A] border-white/5 text-gray-400 hover:border-white/20'
                      }`}
                    >
                      <role.icon className={`w-6 h-6 mb-3 transition-colors ${selectedRole === role.id ? 'text-[#F5A623]' : 'text-gray-500'}`} />
                      <div className="text-sm font-bold block mb-1">{role.name}</div>
                      <div className="text-[10px] uppercase tracking-wider font-medium opacity-60 leading-tight">
                        {role.id === 'restaurant_owner' ? 'Business' : 'Team'}
                      </div>
                      {selectedRole === role.id && (
                        <div className="absolute top-3 right-3">
                          <CheckCircleIcon className="w-5 h-5 text-[#F5A623]" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-gray-500 ml-1">Full Name</label>
                  <div className="relative">
                    <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                    <input
                      type="text"
                      name="fullName"
                      required
                      placeholder="John Doe"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      className="w-full bg-[#0A0A0A] border border-white/5 rounded-xl py-3.5 pl-11 pr-4 text-sm font-medium focus:outline-none focus:border-[#F5A623] transition-colors placeholder:text-gray-700"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-gray-500 ml-1">Phone Number</label>
                  <div className="relative">
                    <PhoneIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                    <input
                      type="tel"
                      name="phone"
                      required
                      placeholder="+91 98765 43210"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full bg-[#0A0A0A] border border-white/5 rounded-xl py-3.5 pl-11 pr-4 text-sm font-medium focus:outline-none focus:border-[#F5A623] transition-colors placeholder:text-gray-700"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-widest text-gray-500 ml-1">Email Address</label>
              <div className="relative">
                <EnvelopeIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="name@restaurant.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full bg-[#0A0A0A] border border-white/5 rounded-xl py-3.5 pl-11 pr-4 text-sm font-medium focus:outline-none focus:border-[#F5A623] transition-colors placeholder:text-gray-700"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-widest text-gray-500 ml-1">Password</label>
              <div className="relative">
                <LockClosedIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  required
                  placeholder="••••••••"
                  minLength={6}
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full bg-[#0A0A0A] border border-white/5 rounded-xl py-3.5 pl-11 pr-12 text-sm font-medium focus:outline-none focus:border-[#F5A623] transition-colors placeholder:text-gray-700"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-400 transition-colors"
                >
                  {showPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#F5A623] hover:bg-[#E0961E] text-black font-bold py-4 rounded-full transition-all transform active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
                ) : (
                  isLogin ? 'Sign In' : 'Create Account'
                )}
              </button>
            </div>
          </form>

          <div className="mt-10 text-center">
            <p className="text-gray-500 text-sm font-medium">
              {isLogin ? "New to Ordyrr?" : "Have an account already?"}{' '}
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-[#F5A623] hover:text-[#E0961E] font-bold transition-colors ml-1"
              >
                {isLogin ? 'Sign Up' : 'Sign In'}
              </button>
            </p>
          </div>
        </motion.div>

        {/* Footer Links */}
        <div className="mt-8 flex justify-center gap-6 text-[10px] uppercase tracking-widest text-gray-600 font-bold">
          <Link to="/privacy" className="hover:text-gray-400 transition-colors">Privacy</Link>
          <Link to="/terms" className="hover:text-gray-400 transition-colors">Terms</Link>
          <Link to="/contact" className="hover:text-gray-400 transition-colors">Support</Link>
        </div>
      </div>
    </div>
  );
};

export default Auth;